import { TEST_IDS } from '@va/constants';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { Spinner } from '@va/icons';
import { useTranslate } from '@va/localization';
import { CustomDomainsApi, SenderEmail } from '@va/shared/api/custom-domains';
import { Button, GrayGradientDivider, ModalHeader, Paragraph, TextInput } from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import { useFormik } from 'formik';
import { useState } from 'react';

export const UpdateCustomSenderNameModal = ({
  onClose,
  isOpen,
  customSenderEmail,
  onSuccess,
}: {
  onClose: () => void;
  isOpen: boolean;
  customSenderEmail: SenderEmail;
  onSuccess?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const translate = useTranslate();
  const { showErrorNotification, showSuccessNotification } = useAddNotification();

  const { submitForm, values, handleChange, handleBlur, errors, setFieldValue, dirty } = useFormik({
    initialValues: {
      senderName: customSenderEmail.name ?? '',
    },
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        await CustomDomainsApi.updateCustomSenderName({
          companyId: customSenderEmail.companyId,
          senderEmailId: customSenderEmail.id,
          senderName: values.senderName,
        });
        onClose();
        onSuccess?.();
        showSuccessNotification();
      } catch (error) {
        console.error(error);
        showErrorNotification();
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <ModalWrapper closeModal={onClose} isModalOpen={isOpen}>
      <ModalHeader
        title={translate('customSenderEmail.updateSenderName.title')}
        onClose={onClose}
        className={'overflow-hidden'}
        titleClassName={'truncate'}
      />
      <div className='space-y-3 p-5 max-w-[43.75rem]'>
        <Paragraph>{translate('customSenderEmail.updateSenderName.line1')}</Paragraph>

        <TextInput
          wrapperClassName='overflow-hidden grow'
          label={translate('customDomain.senderName.input.label')}
          placeholder={translate('customDomain.senderName.input.placeholder')}
          name='senderName'
          value={values.senderName}
          clearField={() => setFieldValue('senderName', '')}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={errors.senderName || undefined}
          showErrorMessage={false}
          bgColor='#F0F0F0'
          bgColorHover='#F9F9F9'
        />
      </div>

      <GrayGradientDivider />

      <div className='flex flex-row justify-end p-3 gap-3'>
        <Button
          className='rounded-12 flex-1'
          color='tertiary'
          text={translate('button.cancel')}
          onClick={onClose}
          data-testid={TEST_IDS.generic.buttons.cancel}
        />
        <Button
          className='rounded-12 flex-2 '
          color='primary'
          disabled={isLoading || !dirty}
          icon={isLoading ? () => <Spinner /> : undefined}
          text={translate('button.saveChanges')}
          onClick={submitForm}
          data-testid={TEST_IDS.generic.buttons.apply}
        />
      </div>
    </ModalWrapper>
  );
};
